// @flow
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { createSelector, type OutputSelector } from 'reselect';

import {
  selectCartProductOwner,
  selectRawProductsArray,
  type DomainProduct,
  type Product,
} from '@gandi/cart';
import { contactbook } from '@gandi/redux-resource-contactbook';
import type {
  ContactRole,
  ExtraParamsByRole,
  ExtraParamsByTld,
  FavoriteContact,
  FavoriteContactByRole,
} from '@gandi/redux-resource-contactbook';
import { selectCurrentId } from '@gandi/redux-sharing';
import { selectors as templateSelectors } from '@gandi/template2';

import type { FullState } from '../../../types';
import { selectDomainOwnerId } from '../../modules/selectors';
import type { CreateOrTransfer, ProcessAndRole, ContactDrafts } from '../types';

// props selector
const selectProcessProp = (
  _: any,
  process: CreateOrTransfer,
): CreateOrTransfer => process;

// owner

export const selectSetupOwner: OutputSelector<FullState, any, string> =
  createSelector(
    [selectCartProductOwner, selectCurrentId],
    (cartOwner, currentId) => cartOwner || currentId || '',
  );

export const selectSetupOwnerChanged: (state: FullState) => boolean = state =>
  // eslint-disable-next-line no-use-before-define
  selectSetupOwner(state) !== selectPayloadSetupOwner(state);

// cart
export const selectHasDomainCreate: (state: FullState) => boolean =
  createSelector(
    [selectRawProductsArray],
    products =>
      !!products.find(
        product =>
          product.productType === 'domain' && product.process === 'create',
      ),
  );

export const selectHasDomainTransfer: (state: FullState) => boolean =
  createSelector(
    [selectRawProductsArray],
    products =>
      !!products.find(
        product =>
          product.productType === 'domain' && product.process === 'transfer',
      ),
  );

export const selectDomainCreate: (state: FullState) => Array<Product> =
  createSelector([selectRawProductsArray], products =>
    products.filter(
      product =>
        product.productType === 'domain' && product.process === 'create',
    ),
  );

export const selectDomainTransfer: (state: FullState) => Array<Product> =
  createSelector([selectRawProductsArray], products =>
    products.filter(
      product =>
        product.productType === 'domain' && product.process === 'transfer',
    ),
  );

export const selectDomainCreateAndTransfer: (
  state: FullState,
) => Array<Product> = createSelector([selectRawProductsArray], products =>
  products.filter(
    product =>
      product.productType === 'domain' &&
      ['create', 'transfer'].includes(product.process),
  ),
);

// payload
// $FlowFixMe[incompatible-call]
const selectPayloadOrgId: (state: FullState) => void | string = createSelector(
  [selectRawProductsArray],
  products => {
    const createOrTransfer = ['transfer', 'create'];
    const domain = products.find(
      product =>
        product.productType === 'domain' &&
        createOrTransfer.includes(product.process),
    );
    if (!domain?.payload) {
      return null;
    }

    return domain.payload.lastCurrentOrgaId;
  },
);

const selectPayloadSetupOwner: (state: FullState) => void | string =
  // $FlowFixMe[incompatible-call]
  createSelector([selectRawProductsArray], products => {
    const createOrTransfer = ['transfer', 'create'];
    const domain = products.find(
      product =>
        product.productType === 'domain' &&
        createOrTransfer.includes(product.process),
    );
    if (!domain?.payload) {
      return null;
    }

    // `setupOwner` should be same has `resellerId` if one
    // else it is the `lastCurrentOrgaId`
    // $FlowFixMe[prop-missing]
    return domain.payload.resellerId || domain.payload.lastCurrentOrgaId;
  });

export const selectIsDomainTransferWithDifferentOwner: (
  FullState,
  owner: FavoriteContact,
) => boolean = createSelector(
  [(_, owner) => owner, selectRawProductsArray],
  (owner, products) =>
    products.filter(
      product =>
        product.productType === 'domain' &&
        product.process === 'transfer' &&
        !!product.payload?.owner &&
        !isEqual(product.payload.owner, owner),
    ).length > 0,
);

// handle case when payload is wrong compared to selected domain owner
export const selectIsPayloadInvalid: (state: FullState) => boolean =
  createSelector(
    [selectPayloadOrgId, selectDomainOwnerId, selectDomainCreateAndTransfer],
    (payloadOrgId, domainOwnerId, domains) =>
      // check owner changed
      payloadOrgId !== domainOwnerId ||
      // check freshly added domain
      !domains.every(domain => !!domain.payload?.lastCurrentOrgaId),
  );

const selectPayloadDomainContactByRole: (
  state: FullState,
  process: CreateOrTransfer,
  // $FlowFixMe[incompatible-call]
) => void | FavoriteContactByRole = createSelector(
  [
    selectDomainCreate,
    selectDomainTransfer,
    selectProcessProp,
    selectIsPayloadInvalid,
  ],
  (domainCreate, domainTransfer, process, isPayloadInvalid) => {
    if (isPayloadInvalid) {
      return null;
    }

    const domains = process === 'create' ? domainCreate : domainTransfer;

    if (domains.length === 0) {
      return null;
    }

    // take the first domain as reference
    let domain: Product | void = domains[0]; // void is due to the find bellow

    // check if we have same tld for every domains, so we can keep it as reference
    const tld = domain?.payload?.tld;
    if (!domains.every(d => d.payload.tld === tld)) {
      // else we have to find one domain without extra_parameters
      domain = domains.find(
        d =>
          !d.payload?.owner?.extra_parameters ||
          Object.keys(d.payload.owner.extra_parameters).length === 0,
      );
    }

    if (!domain || !domain.payload || !domain.payload.owner) {
      return null;
    }

    return {
      admin: domain.payload.admin,
      bill: domain.payload.bill,
      owner: domain.payload.owner,
      tech: domain.payload.tech,
    };
  },
);

const selectPayloadDomainContactRoleProcess: (
  state: FullState,
  args: ProcessAndRole,
) => ?FavoriteContact = (state, args) => {
  const contactByRolePayload = selectPayloadDomainContactByRole(
    state,
    args.process,
  );

  if (!contactByRolePayload) {
    return undefined;
  }

  return contactByRolePayload[args.role];
};

export const selectPayloadOwnerProcess: (
  state: FullState,
  process: CreateOrTransfer,
) => ?FavoriteContact = (state, process) => {
  const contactByRolePayload = selectPayloadDomainContactByRole(state, process);

  if (!contactByRolePayload) {
    return undefined;
  }

  return contactByRolePayload.owner;
};

// draft
export const selectDraftDomainContactByRoles: (
  state: FullState,
  process: CreateOrTransfer,
) => void | FavoriteContactByRole = (state, process) =>
  state.configure.setup.drafts[process];

export const selectHasCreateDraft: (state: FullState) => boolean = state =>
  ['admin', 'tech', 'bill'].some(
    role => state.configure.setup.drafts.create?.[role],
  );

export const selectHasTransferDraft: (state: FullState) => boolean = state =>
  ['admin', 'tech', 'bill'].some(
    role => state.configure.setup.drafts.transfer?.[role],
  );

const selectDraftDomainContactForProcessAndRole: (
  state: FullState,
  args: ProcessAndRole,
) => ?FavoriteContact = (state, processAndRole) => {
  const draft = state.configure.setup.drafts[processAndRole.process];

  return draft?.[processAndRole.role];
};

export const selectContactsWithSameId: (
  state: FullState,
  contactId: string,
) => ContactDrafts = (state, contactId) => {
  const contactsInDrafs: ContactDrafts = {};

  ['create', 'transfer'].forEach(process => {
    const draft = state.configure.setup.drafts[process];
    const payload = selectPayloadDomainContactByRole(state, process);

    if (draft || payload) {
      ['admin', 'bill', 'tech'].forEach(role => {
        const draftRole = draft ? draft[role] : null;
        const payloadRole = payload ? payload[role] : null;
        const contact = draftRole || payloadRole;

        if (contact && contact.id === contactId) {
          if (!contactsInDrafs[process]) {
            contactsInDrafs[process] = {};
          }
          // $FlowFixMe[incompatible-use] :shrug:
          contactsInDrafs[process][role] = contact;
        }
      });
    }
  });

  return contactsInDrafs;
};

// contactbook
const selectFavoriteContactForRole: (
  state: FullState,
  role: ContactRole,
) => ?FavoriteContact = (state, role) => {
  const contactbookOrgId = selectSetupOwner(state);
  return contactbook.selectContactByRole(state, contactbookOrgId)[role];
};

// current roles
export const selectCurrentContactForProcessAndRole: (
  state: FullState,
  args: ProcessAndRole,
) => ?FavoriteContact = (state, args) =>
  selectDraftDomainContactForProcessAndRole(state, args) ||
  selectPayloadDomainContactRoleProcess(state, args) ||
  selectFavoriteContactForRole(state, args.role);

export const selectCurrentContactByRoles: (
  state: FullState,
  process: CreateOrTransfer,
) => FavoriteContactByRole = (state, process) => ({
  admin: selectCurrentContactForProcessAndRole(state, {
    process,
    role: 'admin',
  }),
  bill: selectCurrentContactForProcessAndRole(state, {
    process,
    role: 'bill',
  }),
  tech: selectCurrentContactForProcessAndRole(state, {
    process,
    role: 'tech',
  }),
});

export const selectIsFavoriteContacts: (
  state: FullState,
  process: CreateOrTransfer,
) => boolean = (state, process) =>
  ['admin', 'bill', 'tech'].every(role =>
    isEqual(
      omit(
        selectCurrentContactForProcessAndRole(state, {
          process,
          role,
        }),
        ['extra_parameters'],
      ),
      omit(selectFavoriteContactForRole(state, role), ['extra_parameters']),
    ),
  );

export const selectShouldFetchFavoriteContacts: (
  state: FullState,
  contactbookOrgId: void | string,
  // $FlowFixMe[incompatible-call]
) => boolean = createSelector(
  [
    (_, contactbookOrgId) => contactbookOrgId,
    contactbook.selectContactsWithRoleIsLoaded,
    contactbook.selectContactsWithRoleIsFailure,
    contactbook.selectContactsWithRoleIsFetching,
    contactbook.selectContactsWithRoleIsToRefetch,
  ],
  (
    contactbookOrgId,
    isContactsWithRoleLoaded,
    isContactsWithRoleFailure,
    isFetchingContactsWithRole,
    isContactsWithRoleToRefetch,
  ) =>
    !!(
      !isFetchingContactsWithRole &&
      contactbookOrgId &&
      ((!isContactsWithRoleLoaded && !isContactsWithRoleFailure) ||
        isContactsWithRoleToRefetch)
    ),
);

export const selectShouldFetchOneContactExtraParams: (
  state: FullState,
  args: {
    orgId: void | string,
    contactId: void | string,
  },
  // $FlowFixMe[incompatible-call]
) => boolean = createSelector(
  [
    (_, args) => args,
    contactbook.selectIsLoadedOneContactExtraParams,
    contactbook.selectIsFailureOneContactExtraParams,
    contactbook.selectIsFetchingOneContactExtraParams,
    contactbook.selectIsToRefetchOneContactExtraParams,
  ],
  (
    { orgId, contactId },
    isLoadedOneContactExtraParams,
    isFailureOneContactExtraParams,
    isFetchingOneContactExtraParams,
    isToRefetchOneContactExtraParams,
  ) =>
    !!(
      orgId &&
      contactId &&
      !isFetchingOneContactExtraParams &&
      ((!isLoadedOneContactExtraParams && !isFailureOneContactExtraParams) ||
        isToRefetchOneContactExtraParams)
    ),
);

export const selectShouldFetchOwnerExtraParams: (
  state: FullState,
  args: {
    orgId: void | string,
  },
) => boolean = (state, { orgId }) =>
  !!(
    orgId &&
    !contactbook.selectIsFetchingOwnerContactExtraParams(state, orgId) &&
    ((!contactbook.selectIsLoadedOwnerContactExtraParams(state, orgId) &&
      !contactbook.selectIsFailureOwnerContactExtraParams(state, orgId)) ||
      contactbook.selectIsToRefetchOwnerContactExtraParams(state, orgId))
  );

export const selectShouldFetchOneDefaultTemplate: (
  state: FullState,
  args: {
    productType: 'domain',
    process: CreateOrTransfer,
    sharingId: string,
  },
) => boolean = createSelector(
  [
    (_, args) => args,
    templateSelectors.selectIsLoadedOneDefaultTemplate,
    templateSelectors.selectIsFailureOneDefaultTemplate,
    templateSelectors.selectIsFetchingOneDefaultTemplate,
    templateSelectors.selectIsToRefetchOneDefaultTemplate,
  ],
  (
    { productType, process, sharingId },
    isLoadedOne,
    isFailureOne,
    isFetchingOne,
    isToRefetchOne,
  ) =>
    !!(
      productType &&
      process &&
      sharingId &&
      !isFetchingOne &&
      ((!isLoadedOne && !isFailureOne) || isToRefetchOne)
    ),
);

const isEditedExtraParams = (
  state: FullState,
  product: DomainProduct,
  role: ContactRole,
): boolean => {
  const orgId = selectSetupOwner(state);

  let tldsExtraParams: ExtraParamsByTld = {};

  if (role === 'owner') {
    tldsExtraParams =
      contactbook.selectOwnerContactExtraParams(state, orgId) || {};
  } else {
    const contact = selectCurrentContactForProcessAndRole(state, {
      process: 'create',
      role,
    });
    // select default extra params if the role is a contact from the contactbook
    if (contact && contact.id) {
      tldsExtraParams =
        contactbook.selectContactExtraParams(state, orgId, contact.id) || {};
    }
  }

  const rolePayload = product.payload[role] || {};
  const defaultExtraParams: ExtraParamsByRole =
    // $FlowFixMe refine @gandi/cart ProductPayload
    tldsExtraParams[product.payload.tld] || {};

  return (
    // customs have extra parameters
    !!rolePayload.extra_parameters &&
    // customs have not empty object as extra parameters
    Object.keys(rolePayload.extra_parameters).length > 0 &&
    // customs have extra parameters different to default ones
    !isEqual(rolePayload.extra_parameters, defaultExtraParams[role])
  );
};

// Conflicting TLDs are those that have been modified using the wizard
// and will be overridden when submitting setup
export const selectConflictingTLDs: FullState => Array<string> = state => {
  const tlds = selectRawProductsArray(state)
    .filter(
      product =>
        product.productType === 'domain' &&
        product.process === 'create' &&
        !!product.payload?.tld &&
        ['owner', 'bill', 'admin', 'tech'].some(role =>
          // $FlowFixMe improve @gandi/cart Product
          isEditedExtraParams(state, product, role),
        ),
    )
    .map(domain => domain.payload.tld);

  // $FlowFixMe new Set(tlds) should not be undefined
  return [...new Set(tlds)];
};
